import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { fetchAuthSession } from "aws-amplify/auth";
import "./styles.css";
import "./shared/coralreef/scroll.css";
import { App } from "./App";
import { bootstrapShellHost } from "./shell-handle/bootstrap";
import { createAuthApi } from "./shell-handle/auth";
import { startUrlSync } from "./shell-handle/urlSync";
import { captureReturnTo, consumeReturnTo } from "./shell-handle/returnTo";
import {
  handleSessionExpired,
  handleSessionRefused,
} from "./shell-handle/sessionExpiry";
import {
  bootstrapSession,
  handleOfflineExpiry,
  loadBootSnapshot,
} from "./shell-handle/offlineBoot";
import {
  clearSnapshot,
  offlineModeEnabled,
  readSnapshot,
} from "./shell-handle/offlineSession";
import { hasMarkedOfflineSession } from "./shell-handle/offlineMarker";
import {
  AKVA_TOKEN_KEY,
  TIDE_TOKEN_KEY,
  clearSyncedTokens,
} from "./shell-handle/syncedTokens";
import { onAuthBroadcast } from "./shell-handle/desktopAuthSync";
import { handleAuthBroadcast } from "./shell-handle/authBroadcast";
import { settleBoot } from "./shell-handle/bootGate";
import {
  getEmergencyRole,
  isEmergencyRole,
} from "./exported/shared/utils/emergencySession";
import {
  registerMfes,
  startSingleSpa,
  attachMfeErrorHandler,
} from "./single-spa-config/apps";

const root = document.getElementById("root");
if (!root) throw new Error("Missing #root element");

// Mirror shell path → window.location.hash for compatibility with
// downstream tooling (marlin-v2 e2e tests, desktop-app screen-state
// persistence) that historically relied on tide's hash routing. The
// shell uses tanstack-router with browser history for its real
// routing; this listener just keeps the hash in sync as a side
// channel so `window.location.hash` reads `#/welcome` after the
// shell navigates to `/`.
function pathToHash(pathname: string, search: string): string {
  const target = pathname === "/" ? "/welcome" : pathname;
  return "#" + target + (search || "");
}
function syncHashToPath() {
  const desired = pathToHash(window.location.pathname, window.location.search);
  if (window.location.hash !== desired) {
    history.replaceState(
      history.state,
      "",
      window.location.pathname + window.location.search + desired,
    );
  }
}
syncHashToPath();
window.addEventListener("popstate", syncHashToPath);
const _origPushState = history.pushState.bind(history);
history.pushState = function (...args: Parameters<typeof history.pushState>) {
  _origPushState(...args);
  syncHashToPath();
};
const _origReplaceState = history.replaceState.bind(history);
history.replaceState = function (
  ...args: Parameters<typeof history.replaceState>
) {
  _origReplaceState(...args);
  syncHashToPath();
};

const { host, client, cloudClient, connectivity } = bootstrapShellHost();

// The shell owns window.__OBSERVE_SHELL__ and sets it once at boot. MFEs read
// (never delete) it; if an MFE deletes it on unmount, the next MFE can mount
// into a window where the handle is missing (NO_SHELL), which clobbers its
// site/date context to undefined and crashes. Owning it here keeps it present
// for the whole session regardless of MFE mount/unmount order.
(
  window as unknown as { __OBSERVE_SHELL__?: typeof host.handle }
).__OBSERVE_SHELL__ = host.handle;

function bridgeTokenToMfes(token: string) {
  try {
    localStorage.setItem(TIDE_TOKEN_KEY, token);
    localStorage.setItem(AKVA_TOKEN_KEY, token);
  } catch {
    /* storage may be disabled */
  }
}

// No refresh token is bridged: amplify keeps the real one and the shell renews
// the session itself. The old code wrote the ACCESS token under `refresh_token`,
// which tide then fed to CognitoRefreshToken — a guaranteed NotAuthorized that
// made tide wipe every bridged token and bounce to its own login page.
// Legacy values are still cleared below, along with the offline marker: this
// runs on every teardown, and the other screens read that marker to decide
// whether this machine still has a session.
function clearMfeTokens() {
  clearSyncedTokens();
}

function currentSub(): string | null {
  return host.handle.auth.currentUser.get()?.cognitoSub ?? null;
}

// The session's own snapshot once a user is known, and the station's single
// enrolment before that — the offline boot has no user yet when it asks.
function currentSnapshot() {
  const sub = currentSub();
  return sub ? readSnapshot(sub) : loadBootSnapshot();
}

const auth = createAuthApi();
auth.onTokenRefresh((t) => {
  bridgeTokenToMfes(t);
  // Cognito answered, so the network is back and this session is authenticated
  // again. The stronger of the two resume signals: the probe only proves the
  // pool is reachable, a refreshed token proves the cloud calls will be
  // accepted.
  void host.resumeOnline();
});
auth.onSessionExpired((info) => {
  const deps = {
    clearMfeTokens,
    navigate: (url: string) => window.location.assign(url),
  };
  if (info.refused) {
    void handleSessionRefused(currentSub(), { ...deps, clearSnapshot });
    return;
  }
  handleSessionExpired(info, deps);
});
auth.onOfflineExpiry(() => {
  void handleOfflineExpiry(currentSub(), {
    clearMfeTokens,
    clearSnapshot,
    captureReturnTo,
    navigate: (url) => window.location.assign(url),
  });
});
auth.startTokenWatcher(
  offlineModeEnabled()
    ? {
        probeOnline: () => connectivity.probe(),
        readSnapshot: currentSnapshot,
      }
    : {},
);
// The other resume signal. An offline session that never gets a token refresh
// — hydrated from a snapshot, amplify holding nothing live — would otherwise
// stay offline for the life of the renderer, with every MFE loader reading
// empty cloud stores.
connectivity.onChange((online) => {
  if (online) void host.resumeOnline();
});
{
  const sp = new URLSearchParams(window.location.search);
  const start = sp.get("start");
  const end = sp.get("end");
  if (start || end) host.handle.dateRange.setRange(start, end);
  const sitesRaw = sp.get("sites");
  if (sitesRaw) {
    try {
      const parsed = JSON.parse(sitesRaw);
      if (Array.isArray(parsed))
        host.handle.sites.setSelectedSiteIds(
          parsed.map(Number).filter((n) => Number.isFinite(n)),
        );
    } catch {}
  }
  const cagesRaw = sp.get("cages");
  if (cagesRaw) {
    try {
      const parsed = JSON.parse(cagesRaw);
      if (Array.isArray(parsed))
        host.handle.sites.setSelectedCageIds(parsed.map(String));
    } catch {}
  }
}
// After the one-time boot read above, the shell owns the selectors: every
// later change to start/end/sites/cages is projected back onto the URL.
startUrlSync(host.handle);
attachMfeErrorHandler(host.mfeError);
registerMfes(host.handle);

createRoot(root).render(
  <StrictMode>
    <App
      host={host}
      client={client}
      cloudClient={cloudClient}
      connectivity={connectivity}
    />
  </StrictMode>,
);

function isLoginRoute(): boolean {
  const p = window.location.pathname;
  return p === "/login" || p.startsWith("/login/");
}

function isAuthExempt(): boolean {
  return isLoginRoute() || window.location.pathname.startsWith("/submerged");
}

async function hasActiveSession(): Promise<boolean> {
  try {
    const session = await fetchAuthSession({ forceRefresh: true });
    return !!session.tokens?.idToken;
  } catch {
    return false;
  }
}

// The wall of screens is one login, not one per screen. hasActiveSession()
// forces a refresh, which offline fails for a screen whose session is fine, so
// asking cognito alone would send every other screen to /login the moment one
// of them signed in offline.
async function hasOfflineSession(): Promise<boolean> {
  if (!offlineModeEnabled()) return false;
  return hasMarkedOfflineSession();
}

// A screen only ever hears its own sign-in/sign-out; it has no other way to
// learn that a different screen just did (see routes/login.tsx and
// auth.ts's signOut, which both broadcast). This listens for that broadcast
// and follows the real session either off /login or back onto it.
onAuthBroadcast(() => {
  void handleAuthBroadcast({
    hasActiveSession,
    hasOfflineSession,
    emergencyRole: getEmergencyRole,
    screenEmergencyRole: () => {
      const sub = currentSub();
      return isEmergencyRole(sub) ? sub : null;
    },
    isLoginRoute,
    isAuthExempt,
    consumeReturnTo,
    navigate: (url) => window.location.assign(url),
    reload: () => window.location.reload(),
  });
});

async function fetchIdToken(): Promise<string | null> {
  const session = await fetchAuthSession({ forceRefresh: true });
  return session.tokens?.idToken?.toString() ?? null;
}

// The session died on its own — tear it down without discarding the deep link
// the user was trying to reach.
async function onAuthFailure(): Promise<void> {
  clearMfeTokens();
  try {
    await auth.signOut({ deliberate: false });
  } catch {
    /* ignore */
  }
  if (!isAuthExempt()) {
    captureReturnTo();
    window.location.assign("/login");
  }
}

void (async () => {
  if (isAuthExempt()) {
    settleBoot();
    startSingleSpa();
    return;
  }
  if (auth.emergencyRole) {
    await host.loadInitialData();
    settleBoot();
    startSingleSpa();
    return;
  }
  const decision = await bootstrapSession({
    fetchIdToken,
    bridgeToken: bridgeTokenToMfes,
    loadInitialData: () => host.loadInitialData(),
    hydrateFromSnapshot: (snapshot) => host.hydrateFromSnapshot(snapshot),
    offlineModeEnabled,
    probeOnline: () => connectivity.probe(),
    readSnapshot: loadBootSnapshot,
    onAuthFailure,
  }).finally(settleBoot);
  if (decision !== "login" || isAuthExempt()) {
    startSingleSpa();
  }
  // else: redirect already issued, or there is nothing to run on; do not start
  // single-spa to avoid tide MFE booting against a dead session.
})();
